import { useEffect, useRef, useState } from "react";
import { getPedidoDetalleCliente } from "../../services/usuarios_cliente/perfilpedidos";
import { fileURL } from "../../utils/fileURL";
import { useConfiguracionTiendaPublic } from "../store/hooks/useConfiguracionTiendaPublic";
import "./OrdersModal.css";

const getErrMsg = (error, fallback = "Algo salió mal") => {
  const raw =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message;

  if (!raw) return fallback;
  if (Array.isArray(raw)) return raw.join(", ");
  if (typeof raw === "object") return fallback;
  return String(raw);
};

function ToastStack({ toasts, onClose }) {
  if (!toasts?.length) return null;

  return (
    <div className="adc-toastStack" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`adc-toast adc-toast--${t.type || "info"}`}>
          <div className="adc-toast__body">
            <div className="adc-toast__title">{t.title}</div>
            {t.message ? <div className="adc-toast__msg">{t.message}</div> : null}
          </div>

          <button
            className="adc-toast__close"
            onClick={() => onClose(t.id)}
            aria-label="Cerrar notificación"
            type="button"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";

  return d.toLocaleDateString("es-GT", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const getPaymentBadge = (estadoPago) => {
  const value = String(estadoPago || "").toUpperCase();

  if (value === "PAGADO") {
    return { text: "Pagado", className: "is-paid" };
  }

  if (value === "AUTORIZADO") {
    return { text: "Autorizado", className: "is-paid" };
  }

  if (value === "PENDIENTE") {
    return { text: "Pago pendiente", className: "is-unpaid" };
  }

  if (value === "FALLIDO") {
    return { text: "Pago fallido", className: "is-cancelled" };
  }

  if (value === "REEMBOLSADO") {
    return { text: "Reembolsado", className: "is-unknown" };
  }

  return { text: "Sin registro de pago", className: "is-unpaid" };
};

export default function OrdersModal({ pedidoId, onClose }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const { formatMoney } = useConfiguracionTiendaPublic();

  const [toasts, setToasts] = useState([]);
  const toastTimers = useRef(new Map());

  const pushToast = (type, title, message, opts = {}) => {
    const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const duration = Number.isFinite(opts.duration) ? opts.duration : 4200;

    const toast = { id, type, title, message };
    setToasts((prev) => [toast, ...prev].slice(0, 3));

    const timer = window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      toastTimers.current.delete(id);
    }, duration);

    toastTimers.current.set(id, timer);
    return id;
  };

  const closeToast = (id) => {
    const timer = toastTimers.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      toastTimers.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  useEffect(() => {
    const timers = toastTimers.current;
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setOrder(null);

        const res = await getPedidoDetalleCliente(pedidoId);
        const detail = res?.data || res;

        if (alive) setOrder(detail);
      } catch (e) {
        console.error("Error detalle pedido:", e?.response?.data || e);
        if (alive) setOrder(null);

        pushToast(
          "error",
          "Error al cargar el pedido",
          getErrMsg(e, "No pudimos cargar el detalle del pedido. Intenta de nuevo.")
        );
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [pedidoId]);

  const pedido = order?.pedido || null;
  const direccion = order?.direccion || null;
  const envio = order?.envio || null;
  const pago = order?.pago || null;
  const totales = order?.totales || null;
  const timeline = order?.timeline || null;

  const items = Array.isArray(order?.productos) ? order.productos : [];
  const documentos = Array.isArray(order?.documentos) ? order.documentos : [];

  const statusText = pedido?.estado?.nombre || "—";
  const paymentBadge = getPaymentBadge(pago?.estado_pago);

  return (
    <div className="adc-modal-backdrop adc-orders-scope" onClick={onClose}>
      <div className="adc-orders-css-root">
        <ToastStack toasts={toasts} onClose={closeToast} />

        <div className="adc-modal" onClick={(e) => e.stopPropagation()}>
          <header className="adc-modal-header">
            <div>
              <h2>Pedido #{pedido?.pedido_id || pedidoId}</h2>
              <p className="muted">
                {loading ? "Cargando…" : formatDateTime(pedido?.fecha_creado)}
              </p>
            </div>

            <button className="adc-modal-close" onClick={onClose} type="button">
              ✕
            </button>
          </header>

          <div className="adc-modal-body">
            {loading && <p className="muted">Cargando detalle del pedido…</p>}

            {!loading && !order && (
              <p className="muted">No pudimos cargar este pedido.</p>
            )}

            {!loading && order && (
              <>
                <div className="adc-order-head">
                  <div className="adc-badges">
                    <span className="adc-badge">{statusText}</span>
                    <span className={`adc-badge ${paymentBadge.className}`}>
                      {paymentBadge.text}
                    </span>

                    {pago?.tipo_pago_nombre ? (
                      <span className="adc-badge is-ship">
                        {pago.tipo_pago_nombre}
                      </span>
                    ) : null}
                  </div>

                  <div className="adc-head-grid">
                    <div className="adc-head-item">
                      <span className="muted">Total de factura</span>
                      <strong>
                        {formatMoney(totales?.total_factura)}
                      </strong>
                    </div>

                    <div className="adc-head-item">
                      <span className="muted">Subtotal</span>
                      <strong>{formatMoney(totales?.subtotal)}</strong>
                    </div>

                    <div className="adc-head-item">
                      <span className="muted">Envío</span>
                      <strong>
                        {formatMoney(
                          Number(totales?.costo_envio || 0) +
                            Number(totales?.recargo_envio || 0)
                        )}
                      </strong>
                    </div>

                    <div className="adc-head-item">
                      <span className="muted">Peso total</span>
                      <strong>
                        {totales?.peso_total != null
                          ? `${totales.peso_total} `
                          : "—"}
                      </strong>
                    </div>

                    <div className="adc-head-item">
                      <span className="muted">Método de envío</span>
                      <strong>{envio?.metodo_nombre || "—"}</strong>
                    </div>

                    <div className="adc-head-item">
                      <span className="muted">Numero de cuenta</span>
                      <strong>{envio?.no_cuenta || "—"}</strong>
                    </div>

                    <div className="adc-head-item">
                      <span className="muted">Nombre de cuenta</span>
                      <strong>{envio?.nombre_cuenta || "—"}</strong>
                    </div>

                    <div className="adc-head-item">
                      <span className="muted">Zona</span>
                      <strong>{envio?.zona_nombre || "—"}</strong>
                    </div>

                    <div className="adc-head-item">
                      <span className="muted">Fecha de envío</span>
                      <strong>{formatDateTime(timeline?.fecha_enviado)}</strong>
                    </div>

                    <div className="adc-head-item">
                      <span className="muted">Fecha de entrega</span>
                      <strong>{formatDateTime(timeline?.fecha_entregado)}</strong>
                    </div>
                  </div>
                </div>

                <div className="adc-order-summary">
                  <h3 className="adc-section-title">Envío</h3>

                  <div className="adc-summary-grid">
                    <div className="adc-summary-item">
                      <span className="muted">Destinatario</span>
                      <strong>{direccion?.nombre_destinatario || "—"}</strong>
                    </div>

                    <div className="adc-summary-item">
                      <span className="muted">Teléfono</span>
                      <strong>{direccion?.telefono || "—"}</strong>
                    </div>

                    <div className="adc-summary-item">
                      <span className="muted">Dirección</span>
                      <strong>
                        {direccion?.direccion_linea_1 || "—"}
                        {direccion?.direccion_linea_2
                          ? `, ${direccion.direccion_linea_2}`
                          : ""}
                      </strong>
                    </div>

                    <div className="adc-summary-item">
                      <span className="muted">Ciudad / Municipio</span>
                      <strong>{direccion?.ciudad?.nombre || "—"}</strong>
                    </div>

                    <div className="adc-summary-item">
                      <span className="muted">Region / Departamento</span>
                      <strong>{direccion?.region?.nombre || "—"}</strong>
                    </div>

                    <div className="adc-summary-item">
                      <span className="muted">País</span>
                      <strong>{direccion?.pais?.nombre || "—"}</strong>
                    </div>

                    <div className="adc-summary-item">
                      <span className="muted">Código postal</span>
                      <strong>{direccion?.codigo_postal || "—"}</strong>
                    </div>

                    <div className="adc-summary-item">
                      <span className="muted">Referencia de la dirección</span>
                      <strong>{pago?.referencia || "—"}</strong>
                    </div>
                  </div>
                </div>

                <div className="adc-order-summary">
                  <h3 className="adc-section-title">Pago</h3>

                  <div className="adc-summary-grid">
                    <div className="adc-summary-item">
                      <span className="muted">Método de pago</span>
                      <strong>{pago?.tipo_pago_nombre || "—"}</strong>
                    </div>

                    <div className="adc-summary-item">
                      <span className="muted">Estado del pago</span>
                      <strong>{paymentBadge.text}</strong>
                    </div>
                  </div>
                </div>

                <div className="adc-order-detail">
                  <h3 className="adc-section-title">Productos</h3>

                  {items.length === 0 ? (
                    <p className="muted">No se encontraron productos para este pedido.</p>
                  ) : (
                    <div className="adc-items">
                      {items.map((it) => {
                        const imgSrc = it.url_image ? fileURL(it.url_image) : null;
                        const pdfHref = it.url_documento
                          ? fileURL(it.url_documento)
                          : null;

                        return (
                          <div key={it.pedido_detalles_id} className="adc-item-row">
                            <div className="adc-item-left">
                              <div className="adc-item-img">
                                {imgSrc ? (
                                  <img src={imgSrc} alt={it.nombre_producto} />
                                ) : (
                                  <div className="adc-item-img-placeholder">IMG</div>
                                )}
                              </div>

                              <div className="adc-item-info">
                                <div className="adc-item-name">{it.nombre_producto}</div>

                                <div className="muted">
                                  Cantidad: <strong>{it.cantidad}</strong>
                                  {" · "}
                                  Precio unitario:{" "}
                                  <strong>
                                    {formatMoney(it.precio_unitario)}
                                  </strong>

                                  {it.no_parte ? (
                                    <>
                                      {" · "}SKU: <strong>{it.no_parte}</strong>
                                    </>
                                  ) : null}

                                  {(() => {
                                    const peso = Number(it.peso_producto);
                                    return Number.isFinite(peso) && peso > 0 ? (
                                      <>
                                        {" · "}Peso: <strong>{it.peso_producto}</strong>
                                      </>
                                    ) : null;
                                  })()}
                                </div>

                                {pdfHref && (
                                  <a
                                    className="adc-item-pdf"
                                    href={pdfHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    📄 Documentación
                                  </a>
                                )}
                              </div>
                            </div>

                            <div className="adc-item-right">
                              <span className="muted">Subtotal</span>
                              <strong>{formatMoney(it.total)}</strong>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="adc-order-summary">
                  <h3 className="adc-section-title">Documentos del pedido</h3>

                  {documentos.length === 0 ? (
                    <p className="muted">Este pedido no tiene documentos adjuntos.</p>
                  ) : (
                    <div className="adc-summary-grid">
                      {documentos.map((doc) => (
                        <div key={doc.doc_id} className="adc-summary-item">
                          <span className="muted">{doc.mime || "Documento"}</span>
                          <strong>{doc.filename}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}